import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  NomConnectorObjectType,
  NomObjectClass,
  NomObjectTypes,
} from 'src/app/core/constants';
import { Alert } from 'src/app/core/models/alert.models';
import {
  DataObject,
  ExportSchedule,
  Feed,
  ObjectDefinition,
} from 'src/app/core/models/core.models';

const titleMap = new Map<string, string>([
  [NomObjectClass.FEED + '-' + NomObjectTypes.ONLINE_FEED, 'Online Feed'],
  [NomObjectClass.FEED + '-' + NomObjectTypes.COMBINED_FEED, 'Combined Feed'],
  [NomObjectClass.FEED + '-' + NomObjectTypes.SOCIAL_FEED, 'Social Feed'],
  [NomObjectClass.FEED + '-' + NomObjectTypes.CLIPPING_FEED, 'Clipping Feed'],
  [NomObjectClass.FEED + '-' + NomObjectTypes.BROADCAST_FEED, 'Broadcast Feed'],
  [NomObjectClass.FEED + '-' + NomObjectTypes.STORY_FEED, 'Story Feed'],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.SEARCH_DIMENSION,
    'Search Dimension',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.SOURCE_CATALOG_FEED,
    'Source Catalog',
  ],
  [
    NomObjectClass.WORKSPACE + '-' + NomObjectTypes.WORKSPACE_TYPE_FEEDS,
    'Workspace',
  ],
  [
    NomObjectClass.WORKSPACE + '-' + NomObjectTypes.WORKSPACE_TYPE_CATALOG,
    'Workspace',
  ],
  [NomObjectClass.ALERT + '-' + 0, 'Alert'],
  [NomObjectClass.DASHBOARD + '-' + NomObjectTypes.DASHBOARD_TYPE, 'Dashboard'],
  [NomObjectClass.DASHBOARD + '-' + NomObjectTypes.DASHBOARD, 'Dashboard'],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.CRITICAL_MENTION,
    'Critical Mention',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.FACEBOOK_INSIGHTS,
    'Facebook Insights',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.GOOGLE_ANALYTICS,
    'Google Analytics',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.TWITTER,
    'Twitter (X)',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.EMEDIA_MONITOR,
    'EMedia Monitor',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.SENTINEL_TRAILS,
    'Sentinel Trails',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.TELEGRAM,
    'Telegram',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.INSTAGRAM,
    'Instagram',
  ],
  [NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.TIKTOK, 'TikTok'],
  [NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.YOUTUBE, 'YouTube'],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.FACEBOOK,
    'Facebook',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.VKONTAKTE,
    'VKontakte',
  ],
  [NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.H_TECH, 'H-Tech'],
  [NomObjectClass.EXPORT_SCHEDULE + '-' + 0, 'Export Schedule'],
]);

const iconMap = new Map<string, IconProp>([
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.ONLINE_FEED,
    ['fas', 'earth-americas'],
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.COMBINED_FEED,
    ['fas', 'border-all'],
  ],
  [NomObjectClass.FEED + '-' + NomObjectTypes.SOCIAL_FEED, ['fas', 'comments']],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.CLIPPING_FEED,
    ['fas', 'table-list'],
  ],
  [NomObjectClass.FEED + '-' + NomObjectTypes.BROADCAST_FEED, ['fas', 'radio']],
  [NomObjectClass.FEED + '-' + NomObjectTypes.STORY_FEED, ['fas', 'book-open']],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.SEARCH_DIMENSION,
    ['fas', 'layer-group'],
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.SOURCE_CATALOG_FEED,
    ['fas', 'rss'],
  ],
  [
    NomObjectClass.WORKSPACE + '-' + NomObjectTypes.WORKSPACE_TYPE_FEEDS,
    ['fas', 'folder'],
  ],
  [
    NomObjectClass.WORKSPACE + '-' + NomObjectTypes.WORKSPACE_TYPE_CATALOG,
    ['fas', 'folder'],
  ],
  [
    NomObjectClass.DASHBOARD + '-' + NomObjectTypes.DASHBOARD_TYPE,
    ['fas', 'tachometer-alt'],
  ],
  [
    NomObjectClass.DASHBOARD + '-' + NomObjectTypes.DASHBOARD,
    ['fas', 'tachometer-alt'],
  ],

  [NomObjectClass.FEED + '-' + 0, ['fas', 'earth-americas']],
  [NomObjectClass.DASHBOARD + '-' + 0, ['fas', 'tachometer-alt']],
  [NomObjectClass.ALERT + '-' + 0, ['fas', 'bell']],
  [NomObjectClass.EXPORT_SCHEDULE + '-' + 0, ['fas', 'calendar-check']],
  [NomObjectClass.COMPANY + '-' + 0, ['fas', 'building']],
  [NomObjectClass.RULE + '-' + 2, ['fas', 'list-check']],
  [NomObjectClass.RULE + '-' + 3, ['fas', 'list-check']],
  [NomObjectClass.USER + '-' + 0, ['fas', 'user']],
  [NomObjectClass.USER_GROUP + '-' + 0, ['fas', 'users-cog']],
  [NomObjectClass.ROLE + '-' + 0, ['fas', 'user-shield']],
  [NomObjectClass.WIDGET + '-' + 0, ['fas', 'shapes']],
]);

const imageMap = new Map<string, string>([
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.ONLINE_FEED,
    'assets/img/cards/feed.png',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.COMBINED_FEED,
    'assets/img/cards/feed.png',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.SOCIAL_FEED,
    'assets/img/cards/feed.png',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.CLIPPING_FEED,
    'assets/img/cards/feed.png',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.BROADCAST_FEED,
    'assets/img/cards/feed.png',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.STORY_FEED,
    'assets/img/cards/feed.png',
  ],
  [
    NomObjectClass.FEED + '-' + NomObjectTypes.SEARCH_DIMENSION,
    'assets/img/cards/dimension.png',
  ],
  [
    NomObjectClass.WORKSPACE + '-' + NomObjectTypes.WORKSPACE_TYPE_FEEDS,
    'assets/img/cards/workspace.png',
  ],
  [
    NomObjectClass.WORKSPACE + '-' + NomObjectTypes.WORKSPACE_TYPE_CATALOG,
    'assets/img/cards/workspace.png',
  ],
  [NomObjectClass.ALERT + '-' + 0, 'assets/img/cards/alert.png'],
  [
    NomObjectClass.DASHBOARD + '-' + NomObjectTypes.DASHBOARD_TYPE,
    'assets/img/cards/dashboard.png',
  ],
  [
    NomObjectClass.DASHBOARD + '-' + NomObjectTypes.DASHBOARD,
    'assets/img/cards/dashboard.png',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.FACEBOOK_INSIGHTS,
    'assets/img/connectors/facebook.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.GOOGLE_ANALYTICS,
    'assets/img/connectors/google-analytics.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.TWITTER,
    'assets/img/connectors/x_twitter.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.TELEGRAM,
    'assets/img/connectors/telegram.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.INSTAGRAM,
    'assets/img/connectors/instagram.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.TIKTOK,
    'assets/img/connectors/tiktok.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.YOUTUBE,
    'assets/img/connectors/youtube.svg',
  ],
  [
    NomObjectClass.CONNECTOR + '-' + NomConnectorObjectType.FACEBOOK,
    'assets/img/connectors/facebook.svg',
  ],
  [NomObjectClass.EXPORT_SCHEDULE + '-' + 0, 'assets/img/cards/schedule.png'],
  [NomObjectClass.EXPORT_TEMPLATE + '-' + 0, 'assets/img/cards/export.png'],
]);

/**
 * Returns the value of a nested object prorperty, if exists, or defaultValue, if does not exist
 * @param obj
 * @param path
 * @param defaultValue
 *
 * hasOwnProperty	Safe against prototype pollution
 * != null check	Handles both null and undefined safely
 * Preserves falsy values	Won’t mistakenly override 0, false, '', etc.
 * for loop is 10-15% faster in 100K iterations in Chrome/Node.js
 */
export function getNestedProperty<T>(
  obj: any,
  path: string,
  defaultValue: T,
): T {
  if (!obj || typeof path !== 'string') {
    return defaultValue;
  }

  const keys = path.split('.');

  let result = obj;
  for (const key of keys) {
    if (result != null && Object.prototype.hasOwnProperty.call(result, key)) {
      result = result[key];
    } else {
      return defaultValue;
    }
  }

  return result as T;
}

export function getTitleForDataObject(
  objectClassId: number,
  objectTypeId?: number,
): string {
  const key = `${objectClassId}-${objectTypeId || 0}`;

  return titleMap.get(key) || 'Object'; // default to faFile if no match is found
}

export function getIconForDataObject(
  objectClassId?: number,
  objectTypeId?: number,
): IconProp {
  const key = `${objectClassId}-${objectTypeId || 0}`;

  if (key.includes(NomObjectClass.CONNECTOR.toString())) {
    return ['fas', 'plug'];
  }

  return iconMap.get(key) || ['far', 'file']; // default to faFile if no match is found
}

export function getImageForDataObject(
  objectClassId: number,
  objectTypeId?: number,
): any {
  const key = `${objectClassId}-${objectTypeId || 0}`;

  return imageMap.get(key) || 'assets/img/connectors/connector_default.svg'; // default to faFile if no match is found
}

export function augmentObjectWithIconAndImage(
  dataObject: DataObject | ObjectDefinition,
): any {
  const key = `${dataObject.object_class_id}-${dataObject.object_type_id || 0}`;

  const icon = iconMap.get(key);
  let imageUrl = imageMap.get(key);

  if (dataObject.object_class_id === NomObjectClass.FEED) {
    const object = dataObject as Feed;
    if (object.thumbnail && object.thumbnail.length > 0) {
      imageUrl = object.thumbnail;
    }
  }

  if (!imageUrl) {
    //console.warn(`No image found for ${key}`);
  }

  // Assuming the feed might not always have a corresponding icon or image,
  // and thus, we only set them if they exist.
  return {
    ...dataObject,
    ...(icon && { icon }),
    ...(imageUrl && { imageUrl }),
    //...(color && { color }),
  };
}

export function redirectToObject(
  object: DataObject | Alert | ExportSchedule,
  router: Router,
  newTab: boolean = false,
): void {
  const classMap: Record<number, string> = {
    1: 'feed',
    2: 'workspace',
    20: 'alert',
    21: 'dashboard',
    22: 'export-schedule',
  };

  const objectClassName = classMap[object.object_class_id];
  if (!objectClassName) return;

  const segments: string[] = [
    environment.baseUrlEnvironmentSuffix,
    'analytics',
    'workspace',
  ];

  let queryParams: Record<string, any> | undefined;

  switch (object.object_class_id) {
    case 1:
    case 21:
      if (!('workspace' in object) || !object.workspace?.id) return;
      segments.push(object.workspace.id, objectClassName, object.id);
      break;

    case 2:
      segments.push(object.id);
      break;

    case 20:
    case 22:
      if (!('workspace' in object) || !('context_object' in object)) return;
      const contextObjectClassName =
        classMap[object.context_object.object_class_id];
      segments.push(
        object.workspace.id,
        contextObjectClassName,
        object.context_object.id,
      );
      queryParams = {
        open: objectClassName + 's',
        [`${objectClassName}Id`]: object.id,
      };
      break;

    default:
      return;
  }

  const urlTree = router.createUrlTree(
    segments,
    queryParams ? { queryParams } : undefined,
  );
  if (newTab) {
    window.open(router.serializeUrl(urlTree), '_blank');
  } else {
    void router.navigateByUrl(urlTree);
  }
}

export function augmentObjects(objects: any[]) {
  return objects.map((obj) => ({
    ...augmentObjectWithIconAndImage(obj),
    title: obj.title + '', // Assuming you're ensuring `title` is a string. If obj.title is already a string, this can be just `title: obj.title`.
  }));
}
